import { useState, useRef, useCallback, useEffect } from "react";
import {
  UploadSimple, Robot, PaperPlaneTilt, Spinner, MagnifyingGlass,
  BookOpen, Scales, ArrowsCounterClockwise, Question, X, Plus,
  SidebarSimple, CaretDown, CaretRight, FileText, Image, Trash,
  PencilSimple, Check, ArrowLeft,
} from "@phosphor-icons/react";
import OneDriveFilePicker from "../common/OneDriveFilePicker";
import { Button } from "../ui/button";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../../api";
import MarkdownMessage from "../common/MarkdownMessage";
import NotifyWhenDoneButton from "../common/NotifyWhenDoneButton";
import UploadZone from "./UploadZone";

const ACCEPT_EXTS = [".pdf", ".png", ".jpg", ".jpeg", ".webp", ".bmp", ".tiff", ".tif", ".dwg", ".dxf"];
const ACCEPT_STR = ACCEPT_EXTS.join(",");

const DEFAULT_QUICK_PROMPTS = [
  {
    label: "Services needed for drawing changes",
    text: "Based on the latest drawing (which you should clarify with me), evaluate these drawings and tell me which services I will/MAY need to hire to implement the changes that have been made to the drawings.",
  },
  {
    label: "Compliance check & trades to re-certify",
    text: "Identify all changes between these drawing revisions, then flag which trades (structural, fire, hydraulic, electrical, etc.) would need to re-certify or re-inspect based on those changes, citing the relevant Australian Standards and NCC clauses.",
  },
];

const STORAGE_KEY = "analysis_quick_prompts";

function loadQuickPrompts() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : DEFAULT_QUICK_PROMPTS;
  } catch {
    return DEFAULT_QUICK_PROMPTS;
  }
}

// ── Editable Quick Prompt Card ────────────────────────────────────────────────
function QuickPromptCard({ prompt, index, onUse, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draftLabel, setDraftLabel] = useState(prompt.label);
  const [draftText, setDraftText]   = useState(prompt.text);

  const handleEdit = (e) => { e.stopPropagation(); setDraftLabel(prompt.label); setDraftText(prompt.text); setEditing(true); };
  const handleCancel = () => setEditing(false);
  const handleSave   = () => { onSave(index, { label: draftLabel.trim() || prompt.label, text: draftText.trim() || prompt.text }); setEditing(false); };

  if (editing) {
    return (
      <div
        className="w-full rounded-lg border border-primary/40 bg-primary/[0.02] p-3 space-y-2"
        data-testid={`quick-prompt-edit-${index}`}
        onClick={e => e.stopPropagation()}
      >
        <input
          value={draftLabel}
          onChange={e => setDraftLabel(e.target.value)}
          placeholder="Label"
          className="w-full text-xs font-semibold border border-slate-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-primary/50 bg-white"
          data-testid={`quick-prompt-label-input-${index}`}
        />
        <textarea
          value={draftText}
          onChange={e => setDraftText(e.target.value)}
          placeholder="Prompt text…"
          rows={4}
          className="w-full text-xs border border-slate-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-primary/50 bg-white resize-none leading-relaxed"
          data-testid={`quick-prompt-text-input-${index}`}
        />
        <div className="flex justify-end gap-2">
          <button
            onClick={handleCancel}
            className="text-xs text-slate-500 hover:text-slate-700 px-2 py-1 rounded hover:bg-slate-100 transition-colors"
            data-testid={`quick-prompt-cancel-${index}`}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="text-xs font-medium text-white bg-primary hover:bg-primary/90 px-3 py-1 rounded transition-colors"
            data-testid={`quick-prompt-save-${index}`}
          >
            Save
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="relative w-full text-left px-4 py-3 rounded-lg border border-slate-200 bg-white hover:border-primary/40 hover:bg-primary/[0.02] transition-colors group cursor-pointer"
      onClick={() => onUse(prompt.text)}
      data-testid={`quick-prompt-${index}`}
    >
      <button
        onClick={handleEdit}
        className="absolute top-2.5 right-2.5 opacity-0 group-hover:opacity-100 p-1 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-all"
        title="Edit this suggestion"
        data-testid={`quick-prompt-edit-btn-${index}`}
      >
        <PencilSimple size={12} />
      </button>
      <p className="text-xs font-semibold text-slate-600 group-hover:text-primary transition-colors mb-1 pr-6">
        {prompt.label}
      </p>
      <p className="text-xs text-slate-400 leading-relaxed line-clamp-2">
        {prompt.text}
      </p>
    </div>
  );
}

const TOOL_META = {
  ZOOM_REQUEST:     { icon: MagnifyingGlass, label: "Zoomed into drawing", color: "text-blue-600"   },
  RESEARCH_REQUEST: { icon: BookOpen,        label: "Researched standards",  color: "text-purple-600" },
  ASPECT_REQUEST:   { icon: Scales,          label: "Analysed aspect",       color: "text-amber-600"  },
  COMPARE_REQUEST:  { icon: ArrowsCounterClockwise, label: "Compared drawings", color: "text-green-600" },
  ASK_USER:         { icon: Question,        label: "Asked for input",       color: "text-slate-500"  },
};

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" });
}

// ── Tool Activity Badge ───────────────────────────────────────────────────────
function ToolActivity({ toolUsage }) {
  const [expanded, setExpanded] = useState(false);
  if (!toolUsage || toolUsage.length === 0) return null;

  return (
    <div className="mb-2">
      <button
        onClick={() => setExpanded(v => !v)}
        className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-600 transition-colors"
        data-testid="tool-activity-toggle"
      >
        {expanded ? <CaretDown size={11} /> : <CaretRight size={11} />}
        <span>{toolUsage.length} action{toolUsage.length !== 1 ? "s" : ""} taken</span>
      </button>
      {expanded && (
        <div className="mt-1 pl-3 border-l-2 border-slate-100 space-y-0.5">
          {toolUsage.map((t, i) => {
            const meta = TOOL_META[t.tool] || { icon: Question, label: t.tool, color: "text-slate-400" };
            const Icon = meta.icon;
            let detail = "";
            if (t.tool === "ZOOM_REQUEST")
              detail = `Drawing ${t.args?.drawing ?? 0}, page ${t.args?.page ?? 1}`;
            else if (t.tool === "RESEARCH_REQUEST")
              detail = t.args?.topic || "";
            else if (t.tool === "ASPECT_REQUEST")
              detail = t.args?.aspect || "";
            else if (t.tool === "COMPARE_REQUEST")
              detail = `Drawing ${t.args?.drawing_a ?? 0} vs ${t.args?.drawing_b ?? 1}`;
            return (
              <div key={i} className={`flex items-center gap-1.5 text-xs ${meta.color}`}>
                <Icon size={11} weight="bold" />
                <span>{meta.label}{detail ? ` — ${detail}` : ""}</span>
                {t.status === "failed" && <span className="text-red-400">(failed)</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Chat Bubble ───────────────────────────────────────────────────────────────
function ChatBubble({ msg, toolUsage }) {
  const isUser = msg.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`} data-testid={`chat-bubble-${msg.role}`}>
      <div className={`max-w-[80%] ${isUser ? "message-user px-4 py-3" : "message-ai px-4 py-3"}`}>
        {!isUser && (
          <div className="flex items-center gap-2 mb-1">
            <Robot size={13} className="text-primary" />
            <span className="text-xs font-medium text-primary">AI Assistant</span>
            {msg.timestamp && (
              <span className="text-xs text-slate-300">{formatDate(msg.timestamp)}</span>
            )}
          </div>
        )}
        {!isUser && toolUsage && <ToolActivity toolUsage={toolUsage} />}
        {isUser ? (
          <p className="text-sm leading-relaxed">{msg.content}</p>
        ) : (
          <MarkdownMessage content={msg.content} enableTableExport />
        )}
      </div>
    </div>
  );
}

// ── Drawing Card ──────────────────────────────────────────────────────────────
function DrawingCard({ drawing, index }) {
  const isImage = !drawing.ext || drawing.ext !== ".pdf";
  const Icon = isImage ? Image : FileText;
  return (
    <div
      className="p-3 rounded-lg border border-slate-200 bg-white hover:border-slate-300 transition-colors"
      data-testid={`drawing-card-${index}`}
    >
      <div className="flex items-start gap-2.5">
        <div className="w-12 h-12 rounded bg-slate-100 border border-slate-200 flex-shrink-0 overflow-hidden flex items-center justify-center">
          {drawing.first_page_image ? (
            <img
              src={`data:image/png;base64,${drawing.first_page_image}`}
              alt={drawing.filename}
              className="w-full h-full object-contain"
            />
          ) : (
            <Icon size={20} className="text-slate-400" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-slate-800 truncate" title={drawing.filename}>
            {drawing.filename}
          </p>
          <p className="text-xs text-slate-400 mt-0.5">
            Drawing {index} · {drawing.page_count} page{drawing.page_count !== 1 ? "s" : ""}
          </p>
        </div>
      </div>
    </div>
  );
}

// ── Main Analysis Tool ────────────────────────────────────────────────────────
export default function AnalysisTool({ onBack, restoreSessionId, onStartNew, fasterAnalysis = false, initialFile = null }) {
  const [sessionId, setSessionId] = useState(null);
  const [drawings, setDrawings] = useState([]);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingStartedAt, setLoadingStartedAt] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [renaming, setRenaming] = useState(false);
  const [sessionName, setSessionName] = useState("New Analysis");
  const [nameInput, setNameInput] = useState("");
  const [quickPrompts, setQuickPrompts] = useState(loadQuickPrompts);
  const [showOneDrivePicker, setShowOneDrivePicker] = useState(false);
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);
  const sessionIdRef = useRef(null); // tracks session id without stale-closure issues

  const handleUsePrompt = useCallback((text) => {
    setInput(text);
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.style.height = "auto";
        inputRef.current.style.height = Math.min(inputRef.current.scrollHeight, 128) + "px";
        inputRef.current.focus();
        inputRef.current.setSelectionRange(text.length, text.length);
      }
    }, 0);
  }, []);

  const handleSavePrompt = useCallback((index, updated) => {
    setQuickPrompts(prev => {
      const next = prev.map((p, i) => (i === index ? updated : p));
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);

  // Lazily create a session on first real interaction (upload or send).
  // This prevents blank "0 drawings" sessions from polluting history every
  // time the user simply opens the Analysis tab.
  const ensureSession = useCallback(async () => {
    if (sessionIdRef.current) return sessionIdRef.current;
    const resp = await axios.post(`${API}/analysis/create`);
    if (!resp.data.success) throw new Error("Failed to create analysis session");
    sessionIdRef.current = resp.data.session_id;
    setSessionId(resp.data.session_id);
    return resp.data.session_id;
  }, []);

  // Restore a historical session when restoreSessionId is provided
  useEffect(() => {
    if (!restoreSessionId) return;
    (async () => {
      try {
        const [sessRes, chatRes] = await Promise.all([
          axios.get(`${API}/analysis/${restoreSessionId}`),
          axios.get(`${API}/analysis/${restoreSessionId}/chat`),
        ]);
        const session = sessRes.data.session || {};
        sessionIdRef.current = restoreSessionId;
        setSessionId(restoreSessionId);
        setSessionName(session.display_name || "Analysis Session");
        setDrawings((session.drawings || []).map(d => ({
          slot: d.slot,
          filename: d.filename || `Drawing ${d.slot + 1}`,
          ext: d.ext || ".pdf",
          page_count: d.page_count || 1,
          first_page_image: null,
        })));
        const msgs = (chatRes.data.messages || [])
          .filter(m => m.role === "user" || m.role === "assistant")
          .map(m => ({ role: m.role, content: m.content, timestamp: m.timestamp, toolUsage: m.tool_usage || [] }));
        setMessages(msgs);
      } catch {
        toast.error("Failed to load analysis session");
      }
    })();
  }, [restoreSessionId]);

  // Auto-upload a file passed in from the Explore Files "Ask about drawing" button
  useEffect(() => {
    if (initialFile && !restoreSessionId) {
      handleUpload(initialFile);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally run only once on mount

  // Auto-scroll chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleUpload = useCallback(async (file) => {
    setUploading(true);
    try {
      const sid = await ensureSession();
      const fd = new FormData();
      fd.append("session_id", sid);
      fd.append("file", file);
      const resp = await axios.post(`${API}/analysis/upload`, fd);
      if (resp.data.success) {
        setDrawings(prev => [
          ...prev,
          {
            slot: resp.data.slot,
            filename: file.name,
            ext: "." + file.name.split(".").pop().toLowerCase(),
            page_count: resp.data.page_count,
            first_page_image: resp.data.first_page_image,
          },
        ]);
        toast.success(`"${file.name}" added`);
      }
    } catch (e) {
      toast.error(`Upload failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setUploading(false);
    }
  }, [ensureSession]);

  const handleSend = useCallback(async () => {
    if (!input.trim() || loading || uploading) return;
    const msg = input.trim();
    setInput("");

    const userMsg = { role: "user", content: msg, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg]);
    setLoading(true);
    setLoadingStartedAt(new Date().toISOString());

    try {
      const sid = await ensureSession();
      const resp = await axios.post(`${API}/analysis/chat`, {
        session_id: sid,
        message: msg,
        faster_analysis: fasterAnalysis,
      });
      if (resp.data.success) {
        const aiMsg = {
          role: "assistant",
          content: resp.data.response,
          timestamp: new Date().toISOString(),
          toolUsage: resp.data.tool_usage || [],
          isQuestion: resp.data.is_question || false,
        };
        setMessages(prev => [...prev, aiMsg]);
        // Fetch session to pick up auto-generated name (runs as background task)
        setTimeout(async () => {
          try {
            const sessResp = await axios.get(`${API}/analysis/${sid}`);
            const name = sessResp.data?.session?.display_name;
            if (name) setSessionName(name);
          } catch {}
        }, 2000);
      }
    } catch (e) {
      toast.error(`Error: ${e.response?.data?.detail || e.message}`);
      setMessages(prev => [
        ...prev,
        { role: "assistant", content: "An error occurred. Please try again.", timestamp: new Date().toISOString() },
      ]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  }, [input, loading, uploading, ensureSession, fasterAnalysis]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleRename = async () => {
    const name = nameInput.trim();
    if (!name || !sessionId) return;
    try {
      await axios.patch(`${API}/analysis/${sessionId}/name`, { name });
      setSessionName(name);
      setRenaming(false);
      toast.success("Renamed");
    } catch {
      toast.error("Rename failed");
    }
  };

  const isEmpty = messages.length === 0;

  return (
    <div className="flex h-[calc(100vh-56px)] overflow-hidden bg-slate-50" data-testid="analysis-tool">
      {/* ── Main chat panel ───────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Chat header */}
        <div className="flex items-center justify-between px-5 py-3 bg-white border-b border-slate-200 flex-shrink-0">
          <div className="flex items-center gap-3">
            {renaming ? (
              <div className="flex items-center gap-2">
                <input
                  value={nameInput}
                  onChange={e => setNameInput(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") handleRename(); if (e.key === "Escape") setRenaming(false); }}
                  className="text-sm font-semibold border border-slate-300 rounded px-2 py-0.5 w-52 focus:outline-none focus:ring-1 focus:ring-primary"
                  autoFocus
                  data-testid="rename-input"
                />
                <button onClick={handleRename} className="text-green-600 hover:text-green-700">
                  <Check size={15} />
                </button>
                <button onClick={() => setRenaming(false)} className="text-slate-400 hover:text-slate-600">
                  <X size={15} />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2 group">
                <h2 className="text-sm font-semibold text-slate-800" data-testid="analysis-session-name">
                  {sessionName}
                </h2>
                <button
                  onClick={() => { setNameInput(sessionName); setRenaming(true); }}
                  className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-slate-600 transition-opacity"
                  data-testid="rename-btn"
                >
                  <PencilSimple size={13} />
                </button>
              </div>
            )}
            <span className="text-xs text-slate-400">
              {drawings.length} drawing{drawings.length !== 1 ? "s" : ""}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {restoreSessionId && onStartNew && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setMessages([]);
                  setDrawings([]);
                  setSessionId(null);
                  setSessionName("New Analysis");
                  onStartNew();
                }}
                className="text-xs"
                data-testid="new-analysis-btn"
              >
                + New Analysis
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSidebarOpen(v => !v)}
              className="text-slate-500 hover:text-slate-800"
              data-testid="toggle-sidebar-btn"
              title={sidebarOpen ? "Hide drawings" : "Show drawings"}
            >
              <SidebarSimple size={16} />
            </Button>
          </div>
        </div>

        {/* Chat messages */}
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-4" data-testid="chat-messages">
          {isEmpty && (
            <div className="flex flex-col items-center justify-center h-full text-center gap-4 py-16">
              <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center">
                <Robot size={28} className="text-slate-400" />
              </div>
              <div>
                <p className="font-semibold text-slate-700 text-base">Free-form Analysis</p>
                <p className="text-slate-400 text-sm mt-1 max-w-xs">
                  Upload drawings using the sidebar, then ask anything about them.
                  The AI can zoom in, research standards, compare versions, and more.
                </p>
              </div>
              {drawings.length === 0 && (
                <div className="flex items-center gap-2 text-xs text-slate-400 bg-slate-100 rounded-full px-4 py-2">
                  <UploadSimple size={13} />
                  Add drawings in the sidebar to get started
                </div>
              )}
              {drawings.length > 0 && (
                <p className="text-xs text-primary font-medium bg-primary/5 rounded-full px-4 py-2">
                  {drawings.length} drawing{drawings.length !== 1 ? "s" : ""} ready — ask your first question below
                </p>
              )}

              {/* ── Quick prompt suggestions ── */}
              <div className="w-full max-w-lg mt-2 space-y-2">
                <p className="text-xs text-slate-400 text-center mb-1">Try one of these — click to edit before sending</p>
                {quickPrompts.map((p, i) => (
                  <QuickPromptCard
                    key={i}
                    prompt={p}
                    index={i}
                    onUse={handleUsePrompt}
                    onSave={handleSavePrompt}
                  />
                ))}
              </div>
            </div>
          )}

          {messages.map((msg, i) => (
            <ChatBubble key={i} msg={msg} toolUsage={msg.toolUsage} />
          ))}

          {loading && (
            <div className="flex justify-start" data-testid="loading-indicator">
              <div className="message-ai px-4 py-3 flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <Spinner size={14} className="animate-spin text-primary" />
                  <span className="text-sm text-slate-500">Analysing…</span>
                </div>
                <NotifyWhenDoneButton
                  jobType="analysis"
                  sessionId={sessionIdRef.current}
                  startedAt={loadingStartedAt}
                />
              </div>
            </div>
          )}

          <div ref={chatEndRef} />
        </div>

        {/* Input area */}
        <div className="px-5 py-4 bg-white border-t border-slate-200 flex-shrink-0">
          <div className="flex items-end gap-3">
            <textarea
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={
                uploading
                  ? "Uploading drawing — type your question and send when ready…"
                  : drawings.length === 0
                  ? "Add a drawing first, then ask your question…"
                  : "Ask anything about the drawings… (Enter to send, Shift+Enter for new line)"
              }
              disabled={loading}
              rows={1}
              className="flex-1 resize-none text-sm border border-slate-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-1 focus:ring-primary/50 focus:border-primary/50 bg-white disabled:opacity-50 min-h-[42px] max-h-32 overflow-y-auto"
              style={{ height: "auto" }}
              onInput={e => {
                e.target.style.height = "auto";
                e.target.style.height = Math.min(e.target.scrollHeight, 128) + "px";
              }}
              data-testid="chat-input"
            />
            <Button
              onClick={handleSend}
              disabled={!input.trim() || loading || uploading || !sessionId}
              className="flex-shrink-0 h-[42px] px-4"
              data-testid="send-button"
            >
              <PaperPlaneTilt size={16} weight="fill" />
            </Button>
          </div>
          {uploading ? (
            <p className="text-xs text-amber-500 mt-1.5 pl-0.5 flex items-center gap-1">
              <Spinner size={11} className="animate-spin" />
              Uploading drawing — chat will unlock once upload is complete.
            </p>
          ) : (
            <p className="text-xs text-slate-300 mt-1.5 pl-0.5">
              AI can zoom into drawings, research Australian Standards, compare versions, and analyse specific aspects.
            </p>
          )}
        </div>
      </div>

      {/* ── Drawings sidebar ──────────────────────────────────────────────── */}
      {sidebarOpen && (
        <div
          className="w-64 flex-shrink-0 border-l border-slate-200 bg-white flex flex-col"
          data-testid="drawings-sidebar"
        >
          <div className="px-4 py-3 border-b border-slate-200 flex-shrink-0">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
              Drawings
            </p>
          </div>

          <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2">
            {drawings.length === 0 && (
              <p className="text-xs text-slate-400 text-center py-4">
                No drawings yet.<br />Upload one below.
              </p>
            )}
            {drawings.map((d, i) => (
              <DrawingCard key={d.slot} drawing={d} index={i} />
            ))}
          </div>

          <div className="px-3 py-3 border-t border-slate-200 flex-shrink-0 space-y-2">
            <UploadZone onUpload={handleUpload} uploading={uploading} />
            <button
              onClick={() => setShowOneDrivePicker(true)}
              disabled={uploading}
              className="w-full flex items-center justify-center gap-2 py-2 px-3 rounded-lg border border-slate-200 bg-white text-slate-500 hover:border-[#0078D4] hover:text-[#0078D4] hover:bg-[#0078D4]/5 text-xs font-medium transition-colors disabled:opacity-50"
              data-testid="analysis-onedrive-btn"
            >
              <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 flex-shrink-0" fill="none">
                <path d="M9.5 7.5C10.83 5.3 13.28 4 16 4C19.87 4 23 7.13 23 11C23 11.34 22.97 11.68 22.93 12C23.56 12.35 24 13.03 24 13.83C24 14.92 23.09 15.83 22 15.83H8.5C6.57 15.83 5 14.26 5 12.33C5 10.51 6.38 9.02 8.17 8.86C8.31 8.23 8.85 7.73 9.5 7.5Z" fill="currentColor" opacity="0.6" />
                <path d="M3.5 11C1.57 11 0 12.57 0 14.5C0 16.43 1.57 18 3.5 18H19.5C20.88 18 22 16.88 22 15.5C22 14.12 20.88 13 19.5 13H19C18.45 10.2 16 8 13 8C10.73 8 8.73 9.22 7.69 11.06C6.7 10.42 5.15 10.5 4.5 11H3.5Z" fill="currentColor" />
              </svg>
              Add from OneDrive
            </button>
          </div>
        </div>
      )}

      {/* OneDrive File Picker */}
      {showOneDrivePicker && (
        <OneDriveFilePicker
          onSelect={(file) => handleUpload(file)}
          onClose={() => setShowOneDrivePicker(false)}
        />
      )}
    </div>
  );
}
